/**
 * PipelineTower.tsx
 *
 * Multi-deal aggregate Tower — sister view to Tower.tsx.
 * Shows EVERY active deal in the book plotted on the Level (Checkpoint) it is
 * currently on, plus a fire escape on the LEFT side carrying the deals that
 * exited the building (DISQUALIFIED or BOOTED).
 *
 * Brand: light chrome to match the rest of the app (white canvas, #E5E7EB
 * borders, #0B0B0F text, #DC2626 accents). The Detroit RenCen tower photo
 * column stays on a dark #0B0B0F backdrop — that is intentional art.
 */

import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import { getOrbCpFromRows } from "@shared/scoring/orb-position";

/* ─────────────────────────────────────────────────────────────────────────── */
/* Checkpoint metadata                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */

type Checkpoint = {
  num: number;
  name: string;
  floorLabel: string;
};

const CHECKPOINTS: Checkpoint[] = [
  { num: 1,  name: "Project",                    floorLabel: "Level 1 · Reception" },
  { num: 2,  name: "Need / Pain / Demonstration", floorLabel: "Level 2" },
  { num: 3,  name: "Budget",                     floorLabel: "Level 3" },
  { num: 4,  name: "Timeline",                   floorLabel: "Level 4" },
  { num: 5,  name: "Authority — Project / Initiative",        floorLabel: "Level 5" },
  { num: 6,  name: "Technical and Financial Sign-off",          floorLabel: "Level 6" },
  { num: 7,  name: "Org Structure and Influence Map",   floorLabel: "Level 7" },
  { num: 8,  name: "Competitive Position",        floorLabel: "Level 8" },
  { num: 9,  name: "Compliance — IT Internal and Industry-Specific", floorLabel: "Level 9" },
  { num: 10, name: "Legal — Contracts, SLAs, Terms",                 floorLabel: "Level 10 · Top" },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* Mock book of deals                                                          */
/* ─────────────────────────────────────────────────────────────────────────── */

type Deal = {
  id: string;
  account: string;
  currentCheckpoint: number; // 1..10
};

type ExitReason = "DISQUALIFIED" | "BOOTED";

type ExitedDeal = {
  id: string;
  account: string;
  exitedLevel: number; // 1..10
  reason: ExitReason;
  detail: string;
};

// Track D patch (2026-05-13): OEM hardcoded DEALS removed from real tenant paths.
// Demo tenant (tenant_id=2) continues to use DEMO_DEALS.
// Real tenants see accounts from Supabase with CP levels from account_cp_status.
const DEMO_DEALS: Deal[] = [
  { id: "stellantis",  account: "Stellantis",     currentCheckpoint: 3  },
  { id: "toyota",      account: "Toyota",         currentCheckpoint: 7  },
  { id: "ford",        account: "Ford",           currentCheckpoint: 10 },
  { id: "gm",          account: "GM",             currentCheckpoint: 1  },
  { id: "honda",       account: "Honda",          currentCheckpoint: 4  },
  { id: "tesla",       account: "Tesla",          currentCheckpoint: 2  },
  { id: "hyundai",     account: "Hyundai",        currentCheckpoint: 5  },
  { id: "volkswagen",  account: "Volkswagen",     currentCheckpoint: 6  },
  { id: "mazda",       account: "Mazda",          currentCheckpoint: 1  },
  { id: "subaru",      account: "Subaru",         currentCheckpoint: 3  },
  { id: "nissan",      account: "Nissan",         currentCheckpoint: 8  },
  { id: "bmw",         account: "BMW",            currentCheckpoint: 9  },
  { id: "mercedes",    account: "Mercedes",       currentCheckpoint: 4  },
  { id: "audi",        account: "Audi",           currentCheckpoint: 2  },
  { id: "lucid",       account: "Lucid",          currentCheckpoint: 1  },
];

const EXITED_DEALS: ExitedDeal[] = [
  { id: "rivian",   account: "Rivian",   exitedLevel: 3, reason: "DISQUALIFIED", detail: "No firm timeline surfaced after 4 weeks" },
  { id: "polestar", account: "Polestar", exitedLevel: 5, reason: "BOOTED",       detail: "Champion left for new role" },
  { id: "genesis",  account: "Genesis",  exitedLevel: 2, reason: "DISQUALIFIED", detail: "No path to power identified" },
  { id: "lincoln",  account: "Lincoln",  exitedLevel: 7, reason: "BOOTED",       detail: "Lost to incumbent" },
  { id: "acura",    account: "Acura",    exitedLevel: 4, reason: "DISQUALIFIED", detail: "Budget pulled mid-quarter" },
  { id: "cadillac", account: "Cadillac", exitedLevel: 8, reason: "BOOTED",       detail: "Customer went silent for 6 weeks" },
];

/** Abbreviate an account name for the pill label (first word, capped at 6). */
function abbreviate(name: string): string {
  const first = name.split(/\s+/)[0] ?? name;
  return first.length > 6 ? first.slice(0, 6) : first;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Canon tower image — multi-deal aggregate view                               */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Floor y-positions as percentage of image height (top=0%, bottom=100%).
 * W66.X1 — Re-anchored to actual illuminated rings on the canon tower
 * image via pixel-brightness peak detection. Matches Tower.tsx FLOOR_Y_PCT.
 * 10 detected rings from y=15.4% (crown) to y=57.4% (lowest floor above
 * reception). Each orb sits ON an illuminated ring.
 */
const FLOOR_Y_PCT: Record<number, number> = {
  1: 58.76, 2: 53.71, 3: 48.71, 4: 43.71, 5: 38.71,
  6: 33.77, 7: 28.83, 8: 23.89, 9: 18.95, 10: 14.06,
};

/**
 * Returns the canonical 5-color orb hex for a given composite_score band.
 * W63 — thresholds corrected per 2026-05-23 canon and shared/schema.ts getHealthColorKey:
 *   ≥80  = green (#16A34A)
 *   60–79 = yellow (#EAB308)
 *   40–59 = orange (#EA580C)
 *   1–39  = red (#DC2626)
 *   0 / null = gray (#6B7280)
 */
function orbColor(score?: number | null): string {
  if (!score || score === 0) return "#6B7280"; // gray — not scored
  if (score >= 80) return "#16A34A";           // green — healthy
  if (score >= 60) return "#EAB308";           // yellow — caution
  if (score >= 40) return "#EA580C";           // orange — concern
  return "#DC2626";                             // red — at risk
}

interface OrbDeal extends Deal {
  compositeScore?: number | null;
  cpCoverage?: number | null;
  mpactScore?: string | null;
}

interface DrawerState {
  deal: OrbDeal;
  cp: number;
}

/**
 * LaneT2: CanonAggregateTower — clean tower (no helix) with multi-account orbs.
 *
 * PipelineTower variant: orbs = individual opportunities positioned at the floor
 * matching their current cleared Checkpoint. Multiple deals at same CP =
 * multiple small orbs clustered side-by-side at that floor.
 * Each orb colored by that account's worst-CP health color.
 * Tap an orb → drawer shows account name, owner, CP coverage, M-PACT score, open link.
 */
function CanonAggregateTower({
  deals,
  onOrbClick,
}: {
  deals: OrbDeal[];
  onOrbClick: (deal: OrbDeal) => void;
}) {
  // Group deals by CP level
  const byFloor: Record<number, OrbDeal[]> = {};
  for (let i = 1; i <= 10; i++) byFloor[i] = [];
  for (const d of deals) {
    const cp = Math.max(1, Math.min(10, d.currentCheckpoint));
    byFloor[cp].push(d);
  }

  const orbSize = 14;
  const orbGap = 10;

  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 500, margin: "0 auto" }}>
      {/* LaneT2: Clean tower — no helix inside the spine */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/hero/mi6-tower-clean-desktop.webp" />
        <source media="(max-width: 1023px)" srcSet="/hero/mi6-tower-clean-mobile.webp" />
        <img
          src="/hero/mi6-tower-clean-desktop.webp"
          alt="Mi6 Pipeline Tower"
          style={{ width: "100%", display: "block", borderRadius: 8, objectFit: "contain", objectPosition: "center top" }}
        />
      </picture>

      {/* Multi-account orb overlay per floor */}
      {Object.entries(byFloor).map(([cpStr, floorDeals]) => {
        if (floorDeals.length === 0) return null;
        const cp = Number(cpStr);
        const yPct = FLOOR_Y_PCT[cp] ?? 50;
        // W3-Tower: cluster half-width for L# label placement
        const clusterHalfW = ((floorDeals.length - 1) / 2) * (orbSize + orbGap) + orbSize / 2;
        const labelLeftPx = clusterHalfW + 6;

        return [
          ...floorDeals.map((d, idx) => {
            const color = orbColor(d.compositeScore);
            const offsetPx = (idx - (floorDeals.length - 1) / 2) * (orbSize + orbGap);
            return (
              <div
                key={d.id}
                title={`${d.account} — Level ${cp}`}
                onClick={() => onOrbClick(d)}
                style={{
                  position: "absolute",
                  left: `calc(50% + ${offsetPx}px)`,
                  top: `${yPct}%`,
                  transform: "translate(-50%, -50%)",
                  width: orbSize,
                  height: orbSize,
                  borderRadius: "50%",
                  background: color,
                  boxShadow: `0 0 8px 3px ${color}99`,
                  border: "1.5px solid rgba(255,255,255,0.55)",
                  cursor: "pointer",
                  zIndex: 10,
                  transition: "transform 140ms ease-out",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = "translate(-50%, -50%) scale(1.35)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = "translate(-50%, -50%) scale(1)";
                }}
              />
            );
          }),
          /* W3-Tower: L# label to the right of each occupied floor cluster */
          <div
            key={`lbl-${cp}`}
            aria-label={`Level ${cp}`}
            style={{
              position: "absolute",
              left: `calc(50% + ${labelLeftPx}px)`,
              top: `${yPct}%`,
              transform: "translateY(-50%)",
              fontFamily: "Inter, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              color: "rgba(255,255,255,0.90)",
              letterSpacing: "0.02em",
              textShadow: "0 1px 3px rgba(0,0,0,0.7)",
              zIndex: 11,
              userSelect: "none",
              pointerEvents: "none",
              lineHeight: 1,
            }}
          >
            {`L${cp}`}
          </div>,
        ];
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Page                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

export default function PipelineTower() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredExitId, setHoveredExitId] = useState<string | null>(null);
  const [liveDeals, setLiveDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerDeal, setDrawerDeal] = useState<DrawerState | null>(null);

  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const tenantId = session?.tenantId ?? 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (isDemo) {
        setLiveDeals(DEMO_DEALS);
        setLoading(false);
        return;
      }
      try {
        const sb = supabase();
        const { data: accData } = await sb
          .from("accounts")
          .select("id, name")
          .eq("tenant_id", tenantId);
        if (!accData || accData.length === 0) {
          if (!cancelled) { setLiveDeals([]); setLoading(false); }
          return;
        }
        const accIds = (accData as any[]).map((a: any) => a.id);
        const { data: cpData } = await sb
          .from("account_cp_status")
          .select("account_id, cp_number, status")
          .in("account_id", accIds);
        // W69 P0 Bug 3 Fix: use canonical getOrbCpFromRows (CANON §13.7)
        // Orb = highest n where CP1..CPn are ALL 'green', else 0.
        // Replaces the old "highest green" logic which incorrectly picked a
        // non-contiguous green CP (e.g. CP7=green despite CP4=null).
        const cpRowsByAccount = new Map<number, Array<{ cp_number: number; status: string | null }>>();
        for (const cp of (cpData ?? []) as any[]) {
          const aid = cp.account_id as number;
          if (!cpRowsByAccount.has(aid)) cpRowsByAccount.set(aid, []);
          cpRowsByAccount.get(aid)!.push({ cp_number: cp.cp_number, status: cp.status ?? null });
        }
        const deals: Deal[] = (accData as any[]).map((a: any) => {
          const rows = cpRowsByAccount.get(a.id) ?? [];
          const orbCp = getOrbCpFromRows(rows);
          return {
            id: String(a.id),
            account: a.name,
            currentCheckpoint: Math.max(1, orbCp),
          };
        });
        if (!cancelled) { setLiveDeals(deals); setLoading(false); }
      } catch (err) {
        console.error("[PipelineTower] fetch failed:", err);
        if (!cancelled) { setLiveDeals([]); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [isDemo, tenantId]);

  const DEALS = liveDeals;
  const ACTIVE_EXITED = isDemo ? EXITED_DEALS : [];

  const cpCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const d of DEALS) {
      const cp = Math.max(1, Math.min(10, d.currentCheckpoint));
      counts[cp] = (counts[cp] ?? 0) + 1;
    }
    return counts;
  }, [DEALS]);

  const occupiedCpCount = Object.values(cpCounts).filter((n) => n > 0).length;
  const totalDeals = DEALS.length;
  const exitedCount = ACTIVE_EXITED.length;

  const hoveredDeal = useMemo(
    () => DEALS.find((d) => d.id === hoveredId) ?? null,
    [hoveredId, DEALS]
  );

  const hoveredExitedDeal = useMemo(
    () => ACTIVE_EXITED.find((d) => d.id === hoveredExitId) ?? null,
    [hoveredExitId, ACTIVE_EXITED]
  );

  // Sort deal list: by CP descending (top of tower first), then alphabetical
  const sortedDeals = useMemo(
    () =>
      [...DEALS].sort((a, b) => {
        if (b.currentCheckpoint !== a.currentCheckpoint) {
          return b.currentCheckpoint - a.currentCheckpoint;
        }
        return a.account.localeCompare(b.account);
      }),
    [DEALS]
  );

  // Booted vs Disqualified groupings
  const bootedDeals = ACTIVE_EXITED.filter((d) => d.reason === "BOOTED");
  const disqualifiedDeals = ACTIVE_EXITED.filter(
    (d) => d.reason === "DISQUALIFIED"
  );

  // Exit Level distribution
  const exitLevelCounts = useMemo(() => {
    const m: Record<number, number> = {};
    for (const ed of ACTIVE_EXITED) {
      m[ed.exitedLevel] = (m[ed.exitedLevel] ?? 0) + 1;
    }
    return m;
  }, [ACTIVE_EXITED]);

  const maxExitCount = Math.max(1, ...Object.values(exitLevelCounts));

  return (
    <OperatorShell breadcrumbs={[{ label: "Pipeline Tower" }]} pageTitle="Pipeline Tower">
      <div className="space-y-6">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div>
          <h1
            className="text-3xl text-foreground leading-tight tracking-tight"
            style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
          >
            Qualified pipeline health by checkpoint coverage
          </h1>
          <p className="text-[12px] text-muted-foreground mt-1.5">
            {loading ? "Loading..." : totalDeals > 0 ? `${totalDeals} active deals climbing across ${occupiedCpCount} Levels. ${exitedCount} deals took the fire escape this quarter.${isDemo ? " · Sample data" : ""}` : "Data activates as your team logs activity. Add accounts and score them to populate the Tower."}
          </p>
        </div>

        {/* ── Tower + side panel ──────────────────────────────────────── */}
        <div
          className="rounded-xl border border-border overflow-hidden"
          style={{ background: "#FFFFFF" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left: LaneT2 clean tower (no helix) with multi-account orbs per floor.
                The Detroit RenCen tower photo is intentional art — its column stays
                dark so the orbs and tower render against the photo backdrop. */}
            <div
              className="lg:col-span-5 flex items-center justify-center py-10 px-4"
              style={{ background: "#0B0B0F" }}
            >
              <CanonAggregateTower
                deals={DEALS as OrbDeal[]}
                onOrbClick={(deal) => setDrawerDeal({ deal, cp: deal.currentCheckpoint })}
              />
            </div>

            {/* Right: stats + deal list */}
            <div className="lg:col-span-7 py-10 px-6 border-t lg:border-t-0 lg:border-l border-border">
              {/* Stats summary */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70] mb-1">
                    Active Deals
                  </div>
                  <div
                    className="text-3xl text-[#0B0B0F] leading-tight"
                    style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
                  >
                    {totalDeals}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70] mb-1">
                    Active Levels
                  </div>
                  <div
                    className="text-3xl text-[#0B0B0F] leading-tight"
                    style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
                  >
                    {occupiedCpCount} / 10
                  </div>
                </div>
              </div>

              {/* Hovered deal callout */}
              <div
                className="mb-6 rounded-md border border-border px-3 py-2 min-h-[48px] flex items-center"
                style={{
                  background:
                    hoveredDeal || hoveredExitedDeal ? "#F5F5F4" : "transparent",
                  transition: "background 160ms ease-out",
                }}
              >
                {hoveredDeal ? (
                  <div>
                    <div
                      className="text-sm text-[#0B0B0F]"
                      style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
                    >
                      {hoveredDeal.account}
                    </div>
                    <div className="text-[11px] text-[#6B6B70] mt-0.5">
                      Level {hoveredDeal.currentCheckpoint} —{" "}
                      {CHECKPOINTS[hoveredDeal.currentCheckpoint - 1]?.name}
                    </div>
                  </div>
                ) : hoveredExitedDeal ? (
                  <div>
                    <div
                      className="text-sm text-[#0B0B0F] flex items-center gap-2"
                      style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
                    >
                      {hoveredExitedDeal.account}
                      <span
                        className="text-[10px] tracking-[0.12em] px-1.5 py-0.5 rounded"
                        style={{
                          color: "#DC2626",
                          border: "1px solid #DC2626",
                          background: "rgba(220,38,38,0.08)",
                        }}
                      >
                        {hoveredExitedDeal.reason}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#6B6B70] mt-0.5">
                      Exited Level {hoveredExitedDeal.exitedLevel} —{" "}
                      {hoveredExitedDeal.detail}
                    </div>
                  </div>
                ) : (
                  <div className="text-[12px] text-[#8E8E94]">
                    Hover or tap a pill on the tower to highlight a deal.
                  </div>
                )}
              </div>

              {/* Exited Deals section */}
              <div className="mb-6">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70]">
                    Exited Deals
                  </div>
                  <div
                    className="text-[18px] text-[#0B0B0F] leading-none"
                    style={{ fontFamily: "'DM Sans', Inter, system-ui" }}
                  >
                    {exitedCount}
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Booted */}
                  <div
                    className="rounded-md border px-3 py-2"
                    style={{
                      borderColor: "#E5E7EB",
                      background: "#FAFAF7",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="text-[11px] tracking-[0.12em] uppercase"
                        style={{ color: "#DC2626" }}
                      >
                        Booted — {bootedDeals.length} deals
                      </div>
                    </div>
                    <div className="text-[12px] text-[#0B0B0F]/80 mt-1">
                      {bootedDeals.map((d) => d.account).join(", ")}
                    </div>
                  </div>

                  {/* Disqualified */}
                  <div
                    className="rounded-md border px-3 py-2"
                    style={{
                      borderColor: "#E5E7EB",
                      background: "#FAFAF7",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="text-[11px] tracking-[0.12em] uppercase"
                        style={{ color: "#DC2626" }}
                      >
                        Disqualified — {disqualifiedDeals.length} deals
                      </div>
                    </div>
                    <div className="text-[12px] text-[#0B0B0F]/80 mt-1">
                      {disqualifiedDeals.map((d) => d.account).join(", ")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Exit Level Distribution mini-chart */}
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70] mb-2">
                  Exit Level Distribution
                </div>
                <div className="space-y-1">
                  {[...CHECKPOINTS].reverse().map((cp) => {
                    const count = exitLevelCounts[cp.num] ?? 0;
                    if (count === 0) return null;
                    const pct = (count / maxExitCount) * 100;
                    return (
                      <div
                        key={`exit-${cp.num}`}
                        className="flex items-center gap-3"
                      >
                        <span
                          className="text-[11px] tabular-nums"
                          style={{
                            color: "#6B6B70",
                            minWidth: 56,
                          }}
                        >
                          Level {cp.num}
                        </span>
                        <div
                          className="flex-1 h-[6px] rounded-sm overflow-hidden"
                          style={{ background: "#E5E7EB" }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: "100%",
                              background: "#DC2626",
                              opacity: 0.7,
                            }}
                          />
                        </div>
                        <span
                          className="text-[11px] tabular-nums"
                          style={{ color: "#DC2626", minWidth: 18, textAlign: "right" }}
                        >
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Breakdown per Level */}
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70] mb-2">
                  Breakdown by Checkpoint
                </div>
                <ol className="space-y-1">
                  {[...CHECKPOINTS].reverse().map((cp) => {
                    const count = cpCounts[cp.num] ?? 0;
                    const occupied = count > 0;
                    return (
                      <li
                        key={cp.num}
                        className="flex items-center gap-3 py-1"
                      >
                        <span
                          aria-hidden
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: occupied ? "#DC2626" : "transparent",
                            border: occupied
                              ? "1px solid #DC2626"
                              : "1px solid #E5E7EB",
                            boxShadow: occupied
                              ? "0 0 6px rgba(220,38,38,0.55)"
                              : "none",
                          }}
                        />
                        <span
                          className="text-[12px]"
                          style={{
                            fontFamily: "'DM Sans', Inter, system-ui",
                            color: occupied
                              ? "#0B0B0F"
                              : "#8E8E94",
                            minWidth: 56,
                          }}
                        >
                          Level {cp.num}
                        </span>
                        <span
                          className="text-[12px] flex-1 min-w-0 truncate"
                          style={{
                            color: occupied
                              ? "#0B0B0F"
                              : "#8E8E94",
                          }}
                        >
                          {cp.name}
                        </span>
                        <span
                          className="text-[11px] tabular-nums"
                          style={{
                            color: occupied
                              ? "#DC2626"
                              : "#8E8E94",
                            fontWeight: occupied ? 600 : 400,
                          }}
                        >
                          {count} {count === 1 ? "deal" : "deals"}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Full deal list */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B70] mb-2">
                  All Active Deals
                </div>
                <ul
                  className="space-y-1 max-h-[280px] overflow-y-auto pr-1"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {sortedDeals.map((d) => {
                    const cp = CHECKPOINTS[d.currentCheckpoint - 1];
                    const isHovered = hoveredId === d.id;
                    return (
                      <li
                        key={d.id}
                        onMouseEnter={() => setHoveredId(d.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className="flex items-center justify-between gap-3 px-2 py-1.5 rounded cursor-pointer"
                        style={{
                          background: isHovered
                            ? "rgba(220,38,38,0.10)"
                            : "transparent",
                          borderLeft: isHovered
                            ? "2px solid #DC2626"
                            : "2px solid transparent",
                          transition:
                            "background 140ms ease-out, border-color 140ms ease-out",
                        }}
                      >
                        <span
                          className="text-[13px] text-[#0B0B0F]"
                          style={{
                            fontFamily: "'DM Sans', Inter, system-ui",
                            fontWeight: isHovered ? 600 : 500,
                          }}
                        >
                          {d.account}
                        </span>
                        <span className="text-[11px] text-[#6B6B70] tabular-nums">
                          Level {d.currentCheckpoint} — {cp?.name}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ── Account detail drawer — opens on orb tap (W6 pattern) ─── */}
        {drawerDeal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              height: "100vh",
              width: "min(360px, 90vw)",
              background: "#FFFFFF",
              borderLeft: "1px solid #E5E7EB",
              zIndex: 50,
              overflowY: "auto",
              padding: "32px 24px",
              boxShadow: "-4px 0 24px rgba(15,23,42,0.18)",
            }}
          >
            <button
              onClick={() => setDrawerDeal(null)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "transparent",
                border: "1px solid #E5E7EB",
                borderRadius: 6,
                padding: "4px 10px",
                color: "#6B6B70",
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Close
            </button>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: "#8E8E94", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                Account
              </div>
              <div style={{ fontSize: 22, color: "#0B0B0F", fontFamily: "'DM Sans', Inter, system-ui", fontWeight: 600 }}>
                {drawerDeal.deal.account}
              </div>
            </div>
            <div style={{ marginTop: 20, borderTop: "1px solid #E5E7EB", paddingTop: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#8E8E94", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                    Checkpoint
                  </div>
                  <div style={{ fontSize: 18, color: "#0B0B0F", fontFamily: "'DM Sans', Inter, system-ui" }}>
                    Level {drawerDeal.cp}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B6B70", marginTop: 2 }}>
                    {CHECKPOINTS[drawerDeal.cp - 1]?.name ?? ""}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#8E8E94", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                    CP Coverage
                  </div>
                  <div style={{ fontSize: 18, color: "#0B0B0F", fontFamily: "'DM Sans', Inter, system-ui" }}>
                    {(drawerDeal.deal as OrbDeal).cpCoverage != null
                      ? `${(drawerDeal.deal as OrbDeal).cpCoverage}%`
                      : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#8E8E94", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                    Composite score
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      color: (drawerDeal.deal as OrbDeal).mpactScore ? "#0B0B0F" : "#8E8E94",
                      fontFamily: "'DM Sans', Inter, system-ui",
                    }}
                    title={(drawerDeal.deal as OrbDeal).mpactScore ? undefined : "Score not yet computed"}
                  >
                    {(drawerDeal.deal as OrbDeal).mpactScore || "Computing"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#8E8E94", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>
                    Health
                  </div>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: orbColor((drawerDeal.deal as OrbDeal).compositeScore),
                      display: "inline-block",
                      marginTop: 4,
                      boxShadow: `0 0 8px 2px ${orbColor((drawerDeal.deal as OrbDeal).compositeScore)}99`,
                    }}
                  />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 20, borderTop: "1px solid #E5E7EB", paddingTop: 16 }}>
              <a
                href={`/account/${drawerDeal.deal.id}`}
                style={{
                  display: "inline-block",
                  padding: "8px 16px",
                  background: "#DC2626",
                  color: "#FAFAF7",
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Open account
              </a>
            </div>
          </div>
        )}

        {/* Drawer backdrop */}
        {drawerDeal && (
          <div
            onClick={() => setDrawerDeal(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 49,
            }}
          />
        )}

        {/* ── Footnote ─────────────────────────────────────────────────── */}
        <p className="text-[11px] text-muted-foreground leading-relaxed max-w-2xl">
          The Pipeline Tower shows where every active deal sits in the M-PACT
          methodology. Active deals appear as orbs on the tower — one orb per deal per floor.
          Color reflects composite health, position reflects checkpoint level.
          Multiple deals at the same Checkpoint cluster side-by-side. Tap any orb to view account detail.
        </p>
      </div>
    </OperatorShell>
  );
}
